import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, Chip, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    Tooltip, CircularProgress, Alert, Accordion, AccordionSummary, AccordionDetails,
    Grid, Card, CardContent, CardActions, Divider
} from '@mui/material';
import {
    Add as AddIcon, Delete as DeleteIcon, Lock as LockIcon,
    LockOpen as LockOpenIcon, ExpandMore as ExpandMoreIcon,
    CheckCircle as CheckIcon, Cancel as CancelIcon,
    Description as DescriptionIcon, Factory as FactoryIcon,
    TableChart as TableChartIcon, ArrowForward as ArrowForwardIcon,
    Error as ErrorIcon, Warning as WarningIcon, Refresh as RefreshIcon,
    Visibility as ViewIcon
} from '@mui/icons-material';
import {
    getCompetences, createCompetence, closeCompetence,
    reopenCompetence, deleteCompetence, getBalancete,
    getProduction, getSupplies, validateCompetence, API_URL,
    reprocessCompetence, getCostTableStatus
} from '../api/api';
import axios from 'axios'; // Import axios directly for custom calls if needed
import { useCompetence } from '../contexts/CompetenceContext';

const CompetenceManagementPage = () => {
    const [competences, setCompetences] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [newMonth, setNewMonth] = useState('');
    const [creating, setCreating] = useState(false);

    // State to store detailed status counts for each competence
    // Format: { '2024-01': { balancete: 100, production: 50, supplies: 20 }, ... }
    const [detailsStatus, setDetailsStatus] = useState({});
    const [loadingDetails, setLoadingDetails] = useState({});

    // State to store validation results for each competence
    const [validationStatus, setValidationStatus] = useState({});
    const [loadingValidation, setLoadingValidation] = useState({});

    // State to store processing status for each competence
    const [processingStatus, setProcessingStatus] = useState({});
    const [loadingProcessing, setLoadingProcessing] = useState({});

    // State for reprocessing
    const [reprocessing, setReprocessing] = useState({});

    // State for cost table status per competence
    const [costTableStatus, setCostTableStatus] = useState({});
    const [loadingCostTable, setLoadingCostTable] = useState({});
    // State for close result message
    const [closeResult, setCloseResult] = useState({});

    const { refreshMonths } = useCompetence();

    const fetchCompetences = async () => {
        setLoading(true);
        try {
            const res = await getCompetences();
            setCompetences(res.data);
            setError('');
        } catch (err) {
            console.error("Erro ao carregar competências:", err);
            setError("Erro ao carregar lista de competências.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCompetences();
    }, []);

    // Fetch details for all competences when they load
    useEffect(() => {
        if (competences.length > 0) {
            competences.forEach(comp => {
                if (!detailsStatus[comp.mes] && !loadingDetails[comp.mes]) {
                    fetchCompetenceDetails(comp.mes);
                }
            });
        }
    }, [competences]);

    const fetchCompetenceDetails = async (mes) => {
        if (detailsStatus[mes] || loadingDetails[mes]) return;

        setLoadingDetails(prev => ({ ...prev, [mes]: true }));
        try {
            // Fetch counts in parallel
            const [balRes, prodRes, suppRes] = await Promise.all([
                getBalancete(mes).catch(() => ({ data: [] })),
                axios.get(`${API_URL}/production`, { params: { competencia: mes, per_page: 1 } }).catch(() => ({ data: { total: 0 } })),
                axios.get(`${API_URL}/supplies`, { params: { competencia: mes, per_page: 1 } }).catch(() => ({ data: { total: 0 } }))
            ]);

            setDetailsStatus(prev => ({
                ...prev,
                [mes]: {
                    balancete: balRes.data ? balRes.data.length : 0,
                    production: prodRes.data.total || 0,
                    supplies: suppRes.data.total || 0
                }
            }));
        } catch (err) {
            console.error(`Erro ao buscar detalhes da competência ${mes}:`, err);
        } finally {
            setLoadingDetails(prev => ({ ...prev, [mes]: false }));
        }
    };

    // Fetch validation for a competence
    const fetchValidation = async (mes) => {
        if (validationStatus[mes] || loadingValidation[mes]) return;

        setLoadingValidation(prev => ({ ...prev, [mes]: true }));
        try {
            const res = await validateCompetence(mes);
            setValidationStatus(prev => ({ ...prev, [mes]: res.data }));
        } catch (err) {
            console.error(`Erro ao validar competência ${mes}:`, err);
            setValidationStatus(prev => ({
                ...prev,
                [mes]: { errors: ['Erro ao validar'], warnings: [], valid: false }
            }));
        } finally {
            setLoadingValidation(prev => ({ ...prev, [mes]: false }));
        }
    };

    // Fetch processing status for a competence
    const fetchProcessingStatus = async (mes) => {
        if (processingStatus[mes] || loadingProcessing[mes]) return;

        setLoadingProcessing(prev => ({ ...prev, [mes]: true }));
        try {
            const res = await axios.get(`${API_URL}/competences/${mes}/processing_status`);
            setProcessingStatus(prev => ({ ...prev, [mes]: res.data }));
        } catch (err) {
            console.error(`Erro ao buscar status de processamento ${mes}:`, err);
            setProcessingStatus(prev => ({
                ...prev,
                [mes]: { processed: false, status: 'Erro', has_errors: true }
            }));
        } finally {
            setLoadingProcessing(prev => ({ ...prev, [mes]: false }));
        }
    };

    const handleAccordionChange = (mes) => (event, isExpanded) => {
        if (isExpanded) {
            fetchCompetenceDetails(mes);
            fetchValidation(mes);
            fetchProcessingStatus(mes);
            fetchCostTableStatus(mes);
        }
    };

    const fetchCostTableStatus = async (mes) => {
        if (costTableStatus[mes] || loadingCostTable[mes]) return;
        setLoadingCostTable(prev => ({ ...prev, [mes]: true }));
        try {
            const res = await getCostTableStatus(mes);
            setCostTableStatus(prev => ({ ...prev, [mes]: res.data }));
        } catch (err) {
            // Silently ignore: tabela pode não existir ainda
        } finally {
            setLoadingCostTable(prev => ({ ...prev, [mes]: false }));
        }
    };

    const handleCreate = async () => {
        if (!newMonth) return;
        setCreating(true);
        try {
            await createCompetence(newMonth);
            setOpenDialog(false);
            setNewMonth('');
            fetchCompetences();
            refreshMonths(); // Update global context
        } catch (err) {
            console.error("Erro ao criar competência:", err);
            alert("Erro ao criar competência: " + (err.response?.data?.mensagem || err.message));
        } finally {
            setCreating(false);
        }
    };

    const handleToggleStatus = async (comp, e) => {
        e.stopPropagation(); // Prevent accordion expansion
        const action = comp.status === 'open' ? 'fechar' : 'reabrir';
        if (!window.confirm(`Tem certeza que deseja ${action} a competência ${comp.mes}?`)) return;

        try {
            if (comp.status === 'open') {
                const res = await closeCompetence(comp.mes, comp.company_id);
                // Exibe resumo da tabela de custos gerada
                const tabelaCustos = res.data?.tabela_custos;
                if (tabelaCustos) {
                    setCloseResult(prev => ({ ...prev, [comp.mes]: tabelaCustos }));
                    // Atualiza o status da tabela
                    setCostTableStatus(prev => ({
                        ...prev,
                        [comp.mes]: {
                            existe: (tabelaCustos.total || 0) > 0,
                            total_itens: tabelaCustos.total || 0,
                            insumos: tabelaCustos.insumos || 0,
                            procedimentos: tabelaCustos.procedimentos || 0,
                        }
                    }));
                }
            } else {
                await reopenCompetence(comp.mes, comp.company_id);
                // Limpa o resultado ao reabrir
                setCloseResult(prev => { const n = {...prev}; delete n[comp.mes]; return n; });
                setCostTableStatus(prev => { const n = {...prev}; delete n[comp.mes]; return n; });
            }
            fetchCompetences();
        } catch (err) {
            console.error(`Erro ao ${action} competência:`, err);
            alert(`Erro ao ${action} competência: ` + (err.response?.data?.mensagem || err.response?.data?.error || err.message));
        }
    };

    const handleDelete = async (comp, e) => {
        const mes = comp.mes;
        e.stopPropagation(); // Prevent accordion expansion
        if (!window.confirm(`ATENÇÃO: Isso excluirá TODOS os dados da competência ${mes} (Balancete, Produção, Resultados). Esta ação não pode ser desfeita. Continuar?`)) return;

        try {
            await deleteCompetence(mes, comp.company_id);
            fetchCompetences();
            refreshMonths(); // Update global context
        } catch (err) {
            console.error("Erro ao excluir competência:", err);
            alert("Erro ao excluir competência: " + (err.response?.data?.mensagem || err.message));
        }
    };

    const handleReprocess = async (mes) => {
        if (!window.confirm(`Tem certeza que deseja reprocessar a competência ${mes}? Isso irá sobrescrever os resultados existentes.`)) return;

        setReprocessing(prev => ({ ...prev, [mes]: true }));
        try {
            const res = await reprocessCompetence(mes);

            // Update processing status
            setProcessingStatus(prev => ({
                ...prev,
                [mes]: {
                    processed: true,
                    status: 'Processado',
                    has_errors: false,
                    processing_date: new Date().toISOString()
                }
            }));

            alert(res.data.mensagem || 'Competência reprocessada com sucesso!');
            refreshMonths(); // Update global context
        } catch (err) {
            console.error(`Erro ao reprocessar competência ${mes}:`, err);
            alert(`Erro ao reprocessar: ${err.response?.data?.mensagem || err.message}`);
        } finally {
            setReprocessing(prev => ({ ...prev, [mes]: false }));
        }
    };

    const StatusChip = ({ status }) => {
        const isOpen = status === 'open';
        return (
            <Chip
                label={isOpen ? "Aberto" : "Fechado"}
                color={isOpen ? "success" : "error"}
                size="small"
                icon={isOpen ? <LockOpenIcon /> : <LockIcon />}
            />
        );
    };

    const StatusCard = ({ title, icon, count, link, linkText, loading }) => (
        <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                <Typography variant="subtitle1" component="div" gutterBottom sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                    {icon} {title}
                </Typography>
                <Divider sx={{ my: 1 }} />
                {loading ? (
                    <CircularProgress size={20} />
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        {count > 0 ? (
                            <CheckIcon color="success" sx={{ mr: 1 }} />
                        ) : (
                            <ErrorIcon color="error" sx={{ mr: 1 }} />
                        )}
                        <Typography variant="body2" fontWeight="bold">
                            {count > 0 ? 'Importado' : 'Pendente'}
                        </Typography>
                    </Box>
                )}
                {!loading && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Registros: {count}
                    </Typography>
                )}
            </CardContent>
            <CardActions>
                <Button
                    component={Link}
                    to={link}
                    size="small"
                    fullWidth
                    endIcon={<ArrowForwardIcon />}
                >
                    {linkText}
                </Button>
            </CardActions>
        </Card>
    );

    return (
        <Box p={3} maxWidth={1000} mx="auto">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" fontWeight="bold">Gerenciamento de Competências</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenDialog(true)}
                >
                    Nova Competência
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            ) : competences.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="textSecondary">Nenhuma competência encontrada.</Typography>
                </Paper>
            ) : (
                <Box>
                    {competences.map((comp) => (
                        <Accordion key={comp.mes} onChange={handleAccordionChange(comp.mes)}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box display="flex" alignItems="center" width="100%" justifyContent="space-between" pr={2}>
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Typography variant="h6">{comp.mes}</Typography>
                                        <StatusChip status={comp.status} />

                                        {/* Status Indicators in Summary */}
                                        <Box display="flex" gap={1} ml={2}>
                                            {loadingDetails[comp.mes] ? (
                                                <CircularProgress size={20} />
                                            ) : (
                                                <>
                                                    <Tooltip title={`Balancete: ${detailsStatus[comp.mes]?.balancete || 0} registros`}>
                                                        <Chip
                                                            icon={(detailsStatus[comp.mes]?.balancete || 0) > 0 ? <CheckIcon /> : <CancelIcon />}
                                                            label="Balancete"
                                                            size="small"
                                                            color={(detailsStatus[comp.mes]?.balancete || 0) > 0 ? "success" : "default"}
                                                            variant={(detailsStatus[comp.mes]?.balancete || 0) > 0 ? "filled" : "outlined"}
                                                            sx={{ cursor: 'pointer' }}
                                                        />
                                                    </Tooltip>
                                                    <Tooltip title={`Produção: ${detailsStatus[comp.mes]?.production || 0} registros`}>
                                                        <Chip
                                                            icon={(detailsStatus[comp.mes]?.production || 0) > 0 ? <CheckIcon /> : <CancelIcon />}
                                                            label="Produção"
                                                            size="small"
                                                            color={(detailsStatus[comp.mes]?.production || 0) > 0 ? "success" : "default"}
                                                            variant={(detailsStatus[comp.mes]?.production || 0) > 0 ? "filled" : "outlined"}
                                                            sx={{ cursor: 'pointer' }}
                                                        />
                                                    </Tooltip>
                                                    <Tooltip title={`Insumos: ${detailsStatus[comp.mes]?.supplies || 0} registros`}>
                                                        <Chip
                                                            icon={(detailsStatus[comp.mes]?.supplies || 0) > 0 ? <CheckIcon /> : <CancelIcon />}
                                                            label="Insumos"
                                                            size="small"
                                                            color={(detailsStatus[comp.mes]?.supplies || 0) > 0 ? "success" : "default"}
                                                            variant={(detailsStatus[comp.mes]?.supplies || 0) > 0 ? "filled" : "outlined"}
                                                            sx={{ cursor: 'pointer' }}
                                                        />
                                                    </Tooltip>

                                                    {/* Processing Status Chip */}
                                                    {loadingProcessing[comp.mes] ? (
                                                        <CircularProgress size={20} />
                                                    ) : processingStatus[comp.mes] ? (
                                                        <Tooltip title={
                                                            processingStatus[comp.mes].processed
                                                                ? `Processado em ${processingStatus[comp.mes].processing_date || 'data desconhecida'}`
                                                                : 'Competência não processada'
                                                        }>
                                                            <Chip
                                                                icon={processingStatus[comp.mes].processed ? <CheckIcon /> : <WarningIcon />}
                                                                label={processingStatus[comp.mes].status}
                                                                size="small"
                                                                color={
                                                                    processingStatus[comp.mes].has_errors ? "error" :
                                                                        processingStatus[comp.mes].processed ? "success" : "warning"
                                                                }
                                                                variant="filled"
                                                                sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                                                            />
                                                        </Tooltip>
                                                    ) : null}
                                                </>
                                            )}
                                        </Box>
                                    </Box>
                                    <Box>
                                        <Tooltip title={comp.status === 'open' ? "Fechar Competência" : "Reabrir Competência"}>
                                            <IconButton
                                                onClick={(e) => handleToggleStatus(comp, e)}
                                                color={comp.status === 'open' ? "warning" : "success"}
                                                size="small"
                                            >
                                                {comp.status === 'open' ? <LockIcon /> : <LockOpenIcon />}
                                            </IconButton>
                                        </Tooltip>
                                        {comp.status === 'closed' && (
                                            <Tooltip title="Ver Tabela de Custos">
                                                <IconButton
                                                    component={Link}
                                                    to={`/cost-tables/${comp.mes}`}
                                                    color="primary"
                                                    size="small"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <ViewIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Excluir Competência e Dados">
                                            <IconButton
                                                onClick={(e) => handleDelete(comp, e)}
                                                color="error"
                                                size="small"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ bgcolor: 'background.default', p: 2 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                        <StatusCard
                                            title="Balancete"
                                            icon={<DescriptionIcon color="primary" sx={{ mr: 1 }} />}
                                            count={detailsStatus[comp.mes]?.balancete || 0}
                                            link="/processamento"
                                            linkText="Ir para Balancete"
                                            loading={loadingDetails[comp.mes]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <StatusCard
                                            title="Produção"
                                            icon={<FactoryIcon color="primary" sx={{ mr: 1 }} />}
                                            count={detailsStatus[comp.mes]?.production || 0}
                                            link="/producao"
                                            linkText="Ir para Produção"
                                            loading={loadingDetails[comp.mes]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <StatusCard
                                            title="Insumos"
                                            icon={<TableChartIcon color="primary" sx={{ mr: 1 }} />}
                                            count={detailsStatus[comp.mes]?.supplies || 0}
                                            link="/tabelas-insumo"
                                            linkText="Ir para Insumos"
                                            loading={loadingDetails[comp.mes]}
                                        />
                                    </Grid>

                                    {/* Processing Status Card */}
                                    <Grid item xs={12}>
                                        <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                                            <CardContent>
                                                <Typography variant="subtitle1" component="div" gutterBottom sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                                                    <FactoryIcon color="primary" sx={{ mr: 1 }} /> Status de Processamento
                                                </Typography>
                                                <Divider sx={{ my: 1 }} />
                                                {loadingProcessing[comp.mes] ? (
                                                    <CircularProgress size={20} />
                                                ) : processingStatus[comp.mes]?.processed ? (
                                                    <Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, mb: 1 }}>
                                                            <CheckIcon color="success" sx={{ mr: 1 }} />
                                                            <Typography variant="body2" fontWeight="bold">
                                                                Processado com Sucesso
                                                            </Typography>
                                                        </Box>
                                                        {processingStatus[comp.mes].processing_date && (
                                                            <Typography variant="caption" color="text.secondary" display="block">
                                                                Data: {new Date(processingStatus[comp.mes].processing_date).toLocaleString('pt-BR')}
                                                            </Typography>
                                                        )}
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            Alocações geradas: {processingStatus[comp.mes].allocation_count || 0}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                                        <WarningIcon color="warning" sx={{ mr: 1 }} />
                                                        <Typography variant="body2" fontWeight="bold">
                                                            Pendente de Processamento
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </CardContent>
                                            <CardActions sx={{ display: 'flex', gap: 1 }}>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    size="small"
                                                    startIcon={reprocessing[comp.mes] ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                                                    onClick={() => handleReprocess(comp.mes)}
                                                    disabled={
                                                        reprocessing[comp.mes] ||
                                                        (detailsStatus[comp.mes]?.balancete || 0) === 0 ||
                                                        comp.status === 'closed'
                                                    }
                                                    sx={{ flex: 1 }}
                                                >
                                                    {reprocessing[comp.mes] ? 'Processando...' : comp.status === 'closed' ? 'Fechada (bloqueado)' : 'Reprocessar'}
                                                </Button>
                                                <Button
                                                    component={Link}
                                                    to="/relatorio-conferencia"
                                                    size="small"
                                                    variant="outlined"
                                                    endIcon={<ArrowForwardIcon />}
                                                    disabled={!processingStatus[comp.mes]?.processed}
                                                    sx={{ flex: 1 }}
                                                >
                                                    Ver Relatório
                                                </Button>
                                            </CardActions>
                                        </Card>
                                    </Grid>
                                </Grid>

                                {/* Validation Section */}
                                <Box mt={2}>
                                    <Divider sx={{ mb: 2 }} />
                                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                        Validação de Inconsistências
                                    </Typography>

                                    {loadingValidation[comp.mes] ? (
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <CircularProgress size={20} />
                                            <Typography variant="body2" color="textSecondary">
                                                Verificando inconsistências...
                                            </Typography>
                                        </Box>
                                    ) : validationStatus[comp.mes] ? (
                                        <Box>
                                            {validationStatus[comp.mes].valid &&
                                                validationStatus[comp.mes].errors?.length === 0 &&
                                                validationStatus[comp.mes].warnings?.length === 0 ? (
                                                <Alert severity="success" sx={{ mb: 1 }}>
                                                    <CheckIcon sx={{ mr: 1, fontSize: 18 }} />
                                                    Nenhuma inconsistência encontrada. Competência pronta para processamento.
                                                </Alert>
                                            ) : (
                                                <>
                                                    {/* Errors */}
                                                    {validationStatus[comp.mes].errors?.map((error, idx) => (
                                                        <Alert severity="error" key={`error-${idx}`} sx={{ mb: 1 }}>
                                                            <ErrorIcon sx={{ mr: 1, fontSize: 18 }} />
                                                            {error}
                                                        </Alert>
                                                    ))}

                                                    {/* Warnings */}
                                                    {validationStatus[comp.mes].warnings?.map((warning, idx) => (
                                                        <Alert severity="warning" key={`warning-${idx}`} sx={{ mb: 1 }}>
                                                            <WarningIcon sx={{ mr: 1, fontSize: 18 }} />
                                                            {warning}
                                                        </Alert>
                                                    ))}

                                                    {/* Missing Accounts */}
                                                    {validationStatus[comp.mes].missing_accounts?.length > 0 && (
                                                        <Alert severity="error" sx={{ mb: 1 }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                                                Contas não cadastradas no Plano de Contas:
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                {validationStatus[comp.mes].missing_accounts.map((acc, idx) => (
                                                                    <Chip
                                                                        key={idx}
                                                                        label={acc}
                                                                        size="small"
                                                                        color="error"
                                                                        variant="outlined"
                                                                    />
                                                                ))}
                                                            </Box>
                                                            <Button
                                                                component={Link}
                                                                to="/config"
                                                                size="small"
                                                                sx={{ mt: 1 }}
                                                            >
                                                                Ir para Plano de Contas
                                                            </Button>
                                                        </Alert>
                                                    )}

                                                    {/* Missing Cost Centers */}
                                                    {validationStatus[comp.mes].missing_cost_centers?.length > 0 && (
                                                        <Alert severity="warning" sx={{ mb: 1 }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                                                Centros de Custo não cadastrados:
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                {validationStatus[comp.mes].missing_cost_centers.map((cc, idx) => (
                                                                    <Chip
                                                                        key={idx}
                                                                        label={cc}
                                                                        size="small"
                                                                        color="warning"
                                                                        variant="outlined"
                                                                    />
                                                                ))}
                                                            </Box>
                                                            <Button
                                                                component={Link}
                                                                to="/centros-de-custo"
                                                                size="small"
                                                                sx={{ mt: 1 }}
                                                            >
                                                                Ir para Centros de Custo
                                                            </Button>
                                                        </Alert>
                                                    )}
                                                </>
                                            )}
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" color="textSecondary">
                                            Expanda para carregar validação.
                                        </Typography>
                                    )}
                                </Box>

                                {/* Tabela de Custos Gerada (só para competências fechadas) */}
                                {comp.status === 'closed' && (
                                    <Box mt={2}>
                                        <Divider sx={{ mb: 2 }} />
                                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TableChartIcon color="primary" fontSize="small" />
                                            Tabela de Custos Gerada
                                        </Typography>

                                        {/* Resultado imediato do fechamento */}
                                        {closeResult[comp.mes] && (
                                            <Alert severity={closeResult[comp.mes].aviso ? 'warning' : 'success'} sx={{ mb: 1 }}>
                                                {closeResult[comp.mes].aviso
                                                    ? closeResult[comp.mes].aviso
                                                    : `✅ Tabela de custos gerada: ${closeResult[comp.mes].insumos} insumos + ${closeResult[comp.mes].procedimentos} procedimentos (total: ${closeResult[comp.mes].total} itens)`
                                                }
                                            </Alert>
                                        )}

                                        {/* Status da tabela carregada do banco */}
                                        {loadingCostTable[comp.mes] ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                                {costTableStatus[comp.mes]?.existe ? (
                                                    <>
                                                        <Chip icon={<CheckIcon />} label={`${costTableStatus[comp.mes].total_itens} itens`} color="success" size="small" />
                                                        {costTableStatus[comp.mes].insumos != null && (
                                                            <Chip label={`Insumos: ${costTableStatus[comp.mes].insumos}`} size="small" variant="outlined" color="primary" />
                                                        )}
                                                        {costTableStatus[comp.mes].procedimentos != null && (
                                                            <Chip label={`Procedimentos: ${costTableStatus[comp.mes].procedimentos}`} size="small" variant="outlined" color="secondary" />
                                                        )}
                                                    </>
                                                ) : !closeResult[comp.mes] ? (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Nenhuma tabela de custos encontrada.
                                                    </Typography>
                                                ) : null}
                                                
                                                <Button
                                                    component={Link}
                                                    to={`/cost-tables/${comp.mes}`}
                                                    size="small"
                                                    variant="contained"
                                                    startIcon={<ViewIcon />}
                                                    sx={{ ml: 'auto', textTransform: 'none' }}
                                                >
                                                    Ver Detalhes da Tabela de Custos
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            )}

            {/* Dialog Nova Competência */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle>Nova Competência</DialogTitle>
                <DialogContent>
                    <Box mt={1}>
                        <TextField
                            label="Mês (YYYY-MM)"
                            type="month"
                            fullWidth
                            value={newMonth}
                            onChange={(e) => setNewMonth(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
                    <Button
                        onClick={handleCreate}
                        variant="contained"
                        color="primary"
                        disabled={!newMonth || creating}
                    >
                        {creating ? <CircularProgress size={24} /> : "Criar"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CompetenceManagementPage;
